import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import {
  Box,
  Button,
  TextField,
  Divider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Switch,
  FormHelperText,
} from '@mui/material';
import { t } from 'i18next';
import { paths, USER_SUFFIX } from '@common/types';
import { useGeneral } from './hooks';
import { ContactInformationSection } from '../form/ContactInformationSection';
import { SaveChangesModal, SearchableSelect } from '@components';
import { ProviderSearchFormField } from 'components/DynamicFormFields';
import { Select } from 'ui';

interface GeneralProps {
  provider: paths[`/provider/{record_id}`]['get']['responses']['200']['content']['application/json'];
  setIsProviderInactive: (status: boolean) => void;
}

export const General = ({ provider, setIsProviderInactive }: GeneralProps) => {
  const {
    onSubmit,
    isSubmitEnabled,
    partnerOptions,
    practiceOptions,
    formMethods,
    providerSpecialtyOptions,
    providerRegionOptions,
    providerClinicalOptions,
    setValue,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    regionSearchQuery,
    isRegionOptionsLoading,
    clinicalSearchQuery,
    isClinicalOptionsLoading,
    handleChangeRegionQuery,
    handleChangeClinicalQuery,
  } = useGeneral(provider);
  const {
    control,
    formState: { isSubmitting },
    watch,
  } = formMethods;

  const isProviderInactive = watch('inactive');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            height: 'fit-content',
            borderRadius: 0,
            backgroundColor: 'white',
            position: 'relative',
            gap: '1rem',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: '0.5rem',
            }}
          >
            <Controller
              name='first_name'
              control={control}
              disabled={isProviderInactive}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  size='medium'
                  label={t('adminPanel.providers.addModal.firstName')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name='last_name'
              control={control}
              disabled={isProviderInactive}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  size='medium'
                  label={t('adminPanel.providers.addModal.lastName')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />
          </Box>

          <Controller
            name='suffix'
            control={control}
            disabled={isProviderInactive}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                size='medium'
                label={t('adminPanel.providers.addModal.optionalSuffix')}
                error={!isNil(error)}
                options={Object.values(USER_SUFFIX).map(o => ({
                  value: o,
                  label: o,
                }))}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='clinical_leader_id'
            control={control}
            disabled={isProviderInactive}
            render={({ field, fieldState: { error } }) => (
              <SearchableSelect
                {...field}
                label={t('adminPanel.providers.addModal.clinical_leader_id')}
                error={!isNil(error)}
                onChange={value => field.onChange(value)}
                options={providerClinicalOptions}
                helperText={error?.message}
                searchQuery={clinicalSearchQuery}
                onChangeSearchQuery={handleChangeClinicalQuery}
                isLoading={isClinicalOptionsLoading}
                disabled={isProviderInactive}
              />
            )}
          />

          <Controller
            name='external'
            control={control}
            disabled={isProviderInactive}
            defaultValue={false}
            render={({ field: { onChange, value, disabled } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!value}
                    disabled={disabled}
                    onChange={(_, checked) => onChange(checked)}
                  />
                }
                label={t('adminPanel.providers.addModal.external')}
                sx={{ alignSelf: 'flex-start' }}
              />
            )}
          />
          <Controller
            name='provider_specialty'
            control={control}
            disabled={isProviderInactive}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                size='medium'
                label={t(
                  'adminPanel.providers.addModal.providerSpecialtyLabel',
                )}
                error={!isNil(error)}
                options={providerSpecialtyOptions}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='region_id'
            control={control}
            disabled={isProviderInactive}
            render={({ field, fieldState: { error } }) => (
              <SearchableSelect
                {...field}
                label={t('adminPanel.providers.addModal.region')}
                error={!isNil(error)}
                onChange={value => field.onChange(value)}
                options={providerRegionOptions}
                helperText={error?.message}
                searchQuery={regionSearchQuery}
                onChangeSearchQuery={handleChangeRegionQuery}
                isLoading={isRegionOptionsLoading}
                disabled={isProviderInactive}
              />
            )}
          />

          <ProviderSearchFormField
            {...control}
            name='supervising_physician_id'
            placeholder={t(
              'patient.careTeam.modal.labels.supervising_physician_id',
            )}
            optional
            disabled={isProviderInactive}
          />

          <Divider sx={{ width: '100%' }} />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: '0.5rem',
            }}
          >
            <Controller
              name='partner_id'
              control={control}
              disabled={isProviderInactive}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.providers.addModal.partner')}
                  error={!isNil(error)}
                  required
                  onChange={event => {
                    field.onChange(event);
                    setValue('practice_id', '');
                  }}
                  options={partnerOptions}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name='practice_id'
              control={control}
              disabled={isProviderInactive}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.providers.addModal.practice')}
                  error={!isNil(error)}
                  required
                  options={practiceOptions}
                  helperText={error?.message}
                />
              )}
            />
          </Box>

          <ContactInformationSection />

          <Controller
            name='inactive'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={(event, checked) => {
                          field.onChange(event);
                          setIsProviderInactive(checked);
                        }}
                      />
                    }
                    label={t('adminPanel.providers.addModal.inactive')}
                  />
                </FormGroup>
                <FormHelperText error={!isNil(error)}>
                  {error ? error.message : null}
                </FormHelperText>
              </FormControl>
            )}
          />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              pt: '0.5rem',
              position: 'sticky',
              bottom: '0',
              left: 0,
              bgcolor: theme => theme.palette.common.white,
              zIndex: 10,
            }}
          >
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
              disabled={!isSubmitEnabled}
              loading={isSubmitting}
              loadingPosition='center'
            >
              {t('general.saveChanges')}
            </Button>
          </Box>
        </Form>
      </FormProvider>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </Box>
  );
};
